import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authenticatedFetch } from '../services/authService';
import { socketService } from '@platform/socket';
import { X, Send, Search, MessageSquare } from '@ui/icons';

export interface ChatUser {
  id: number;
  imie?: string;
  nazwisko?: string;
  login: string;
  email?: string;
  display_name: string;
  initials: string;
}

export interface ChatMessage {
  id: number;
  sender_id: number;
  receiver_id: number;
  message: string;
  read: boolean;
  created_at: string;
}

interface ChatPanelProps {
  isOpen: boolean;
  onClose: () => void;
  selectedUserId?: number | null;
  onSelectUser?: (userId: number) => void;
}

/**
 * ChatPanel component - Full chat interface with user list and message window
 * Similar to Tailadmin chat design
 */
export const ChatPanel: React.FC<ChatPanelProps> = ({
  isOpen,
  onClose,
  selectedUserId: initialSelectedUserId,
  onSelectUser
}) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<any[]>([]);
  const [users, setUsers] = useState<ChatUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(initialSelectedUserId || null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Load conversations list
  const loadConversations = useCallback(async () => {
    if (!user) return;
    try {
      const response = await authenticatedFetch('/api/chat/conversations', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setConversations(data.conversations || []);
        }
      }
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, [user]);

  // Load users list
  const loadUsers = useCallback(async () => {
    if (!user) return;
    try {
      const response = await authenticatedFetch('/api/chat/users', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setUsers(data.users || []);
        }
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  }, [user]);

  // Load messages for selected user
  const loadMessages = useCallback(async (userId: number) => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(`/api/chat/messages/${userId}?limit=100`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Remove duplicates based on message ID
          const uniqueMessages = (data.messages || []).reduce((acc: ChatMessage[], msg: ChatMessage) => {
            if (!acc.find(m => m.id === msg.id)) {
              acc.push(msg);
            }
            return acc;
          }, []);
          setMessages(uniqueMessages);
          // Scroll to bottom after messages load
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          setError('Nie udało się załadować wiadomości');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Błąd podczas ładowania wiadomości');
      }
    } catch (err) {
      console.error('Error loading messages:', err);
      setError('Błąd połączenia. Sprawdź połączenie z internetem.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Send message
  const sendMessage = useCallback(async () => {
    if (!user || !selectedUserId || !newMessage.trim() || sending) return;

    const messageText = newMessage.trim();
    setNewMessage('');
    setSending(true);
    setError(null);
    
    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
    }

    try {
      const response = await authenticatedFetch('/api/chat/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          receiver_id: selectedUserId,
          message: messageText
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Add message to local state immediately for instant feedback
          const newMsg: ChatMessage = {
            ...data.message,
            sender_id: user.id,
            receiver_id: selectedUserId
          };
          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(m => m.id === newMsg.id)) return prev;
            return [...prev, newMsg];
          });
          // Reload conversations to update last message
          loadConversations();
          // Scroll to bottom
          setTimeout(() => {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        } else {
          setError('Nie udało się wysłać wiadomości');
          setNewMessage(messageText);
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.message || 'Błąd podczas wysyłania wiadomości');
        setNewMessage(messageText);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError('Błąd połączenia. Sprawdź połączenie z internetem.');
      // Restore message text on error
      setNewMessage(messageText);
    } finally {
      setSending(false);
    }
  }, [user, selectedUserId, newMessage, sending, loadConversations]);

  // Handle user selection
  const handleSelectUser = useCallback((userId: number) => {
    setSelectedUserId(userId);
    setMessages([]);
    setError(null);
    setNewMessage('');
    loadMessages(userId);
    if (onSelectUser) {
      onSelectUser(userId);
    }
  }, [loadMessages, onSelectUser]);

  // Socket.IO realtime updates
  useEffect(() => {
    if (!isOpen || !user) return;

    const handleDbChange = (payload: any) => {
      try {
        if (payload.table === 'chat_message') {
          const msg = payload.row;
          if (!msg) return;

          // Check if this message is relevant to current user
          const isRelevant = 
            (msg.sender_id === user.id && msg.receiver_id === selectedUserId) ||
            (msg.receiver_id === user.id && msg.sender_id === selectedUserId);

          if (isRelevant && payload.operation === 'INSERT') {
            // Add new message if viewing this conversation
            if (selectedUserId && (msg.sender_id === selectedUserId || msg.receiver_id === selectedUserId)) {
              setMessages(prev => {
                // Avoid duplicates
                if (prev.some(m => m.id === msg.id)) return prev;
                return [...prev, msg];
              });
              // Scroll to bottom
              setTimeout(() => {
                messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }
            // Reload conversations to update last message
            loadConversations();
          } else if (isRelevant && payload.operation === 'UPDATE') {
            // Update message (read status or other changes)
            setMessages(prev =>
              prev.map(m => m.id === msg.id ? { ...m, ...msg } : m)
            );
          }
        }
      } catch (err) {
        console.error('[ChatPanel] Error processing socket event:', err);
      }
    };

    const unsubscribe = socketService.subscribe(handleDbChange);

    return () => {
      unsubscribe();
    };
  }, [isOpen, user, selectedUserId, loadConversations]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '44px';
      if (newMessage) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  }, [newMessage]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messages.length > 0 && messagesEndRef.current) {
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [messages.length]);

  // Load data when panel opens
  useEffect(() => {
    if (isOpen && user) {
      setError(null);
      loadConversations();
      loadUsers();
      if (initialSelectedUserId) {
        handleSelectUser(initialSelectedUserId);
      }
    }
  }, [isOpen, user, initialSelectedUserId, loadConversations, loadUsers, handleSelectUser]);

  // Filter users/conversations by search
  const filteredConversations = conversations.filter(conv =>
    conv.other_user_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Combine conversations and users (show users without conversations)
  const allContacts = [
    ...filteredConversations.map(conv => ({
      id: conv.other_user_id,
      name: conv.other_user_name,
      initials: conv.other_user_initials,
      lastMessage: conv.last_message,
      lastMessageTime: conv.last_message_time,
      unreadCount: conv.unread_count,
      isConversation: true
    })),
    ...filteredUsers
      .filter(u => !conversations.some(c => c.other_user_id === u.id))
      .map(u => ({
        id: u.id,
        name: u.display_name,
        initials: u.initials,
        lastMessage: null,
        lastMessageTime: null,
        unreadCount: 0,
        isConversation: false
      }))
  ].sort((a, b) => {
    // Sort: conversations with unread first, then by last message time
    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
    if (a.lastMessageTime && b.lastMessageTime) {
      return new Date(b.lastMessageTime).getTime() - new Date(a.lastMessageTime).getTime();
    }
    if (a.lastMessageTime) return -1;
    if (b.lastMessageTime) return 1;
    return a.name.localeCompare(b.name);
  });

  const formatTime = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      
      if (diffMins < 1) return 'teraz';
      if (diffMins < 60) return `${diffMins} min temu`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours}h temu`;
      
      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return `${diffDays}d temu`;
      
      return date.toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  const selectedUser = users.find(u => u.id === selectedUserId) || 
    conversations.find(c => c.other_user_id === selectedUserId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50">
      <div className="w-full h-full max-w-6xl max-h-[90vh] bg-white rounded-lg shadow-xl flex flex-col" style={{ backgroundColor: '#ffffff' }}>
        {/* Header */}
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <h2 className="text-lg font-semibold text-text">Czat</h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center text-text hover:text-accent transition-colors p-1 cursor-pointer"
            aria-label="Zamknij"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar - Users/Conversations List */}
          <div className="w-80 border-r border-border flex flex-col">
            {/* Search */}
            <div className="p-4 border-b border-border">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted" />
                <input
                  type="text"
                  placeholder="Szukaj..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border border-border rounded-md bg-card text-text focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Users/Conversations List */}
            <div className="flex-1 overflow-y-auto">
              {allContacts.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted">
                  <p>Brak kontaktów</p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {allContacts.map((contact) => {
                    const isSelected = contact.id === selectedUserId;
                    return (
                      <div
                        key={contact.id}
                        onClick={() => handleSelectUser(contact.id)}
                        className={`px-4 py-3 cursor-pointer transition-colors ${
                          isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white font-semibold text-sm">
                            {contact.initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <p className={`text-sm font-medium ${isSelected ? 'text-blue-600' : 'text-text'}`}>
                                {contact.name}
                              </p>
                              {contact.unreadCount > 0 && (
                                <span className="flex-shrink-0 flex items-center justify-center min-w-[20px] h-[20px] px-1.5 text-[10px] font-bold text-white bg-blue-500 rounded-full">
                                  {contact.unreadCount > 99 ? '99+' : contact.unreadCount}
                                </span>
                              )}
                            </div>
                            {contact.lastMessage && (
                              <>
                                <p className="text-xs text-muted line-clamp-1 mb-1">
                                  {contact.lastMessage}
                                </p>
                                <p className="text-xs text-muted">
                                  {formatTime(contact.lastMessageTime!)}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Chat Messages */}
          <div className="flex-1 flex flex-col">
            {selectedUserId ? (
              <>
                {/* Chat Header */}
                <div className="px-6 py-4 border-b border-border flex items-center gap-3">
                  <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 text-white font-semibold text-sm">
                    {selectedUser?.initials || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-text">
                      {selectedUser?.display_name || selectedUser?.name || 'Użytkownik'}
                    </p>
                    <p className="text-xs text-muted">Aktywny</p>
                  </div>
                </div>

                {/* Messages Area */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto px-6 py-4 bg-gray-50"
                  style={{ backgroundColor: '#f9fafb' }}
                >
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-sm text-red-600">{error}</p>
                    </div>
                  )}
                  {loading ? (
                    <div className="text-center text-sm text-muted py-8">Ładowanie wiadomości...</div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-sm text-muted py-8">
                      <p>Brak wiadomości</p>
                      <p className="text-xs mt-2">Rozpocznij konwersację</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {messages.map((msg) => {
                        const isOwn = msg.sender_id === user?.id;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-lg px-4 py-2 ${
                                isOwn
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-white text-text border border-border'
                              }`}
                            >
                              <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                              <p className={`text-xs mt-1 ${isOwn ? 'text-blue-100' : 'text-muted'}`}>
                                {formatTime(msg.created_at)}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <div className="px-6 py-4 border-t border-border bg-white">
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      sendMessage();
                    }}
                    className="flex items-end gap-3"
                  >
                    <textarea
                      ref={textareaRef}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          sendMessage();
                        }
                      }}
                      placeholder="Napisz wiadomość..."
                      rows={1}
                      disabled={sending}
                      className="flex-1 px-4 py-2 text-sm border border-border rounded-lg bg-card text-text focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ minHeight: '44px', maxHeight: '120px', overflowY: 'auto' }}
                    />
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                      aria-label="Wyślij"
                    >
                      <Send className="w-5 h-5" />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gray-50">
                <div className="text-center text-muted">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-sm">Wybierz użytkownika, aby rozpocząć konwersację</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

